import type { IEvaultApi } from './use-evault-api';
import type {
	ICheckoutCommitted,
	ICheckoutInitiated,
	IVaultDocument,
} from './types';

import { useCallback, useRef, useState } from 'react';

import { EVAULT_API } from './use-evault-api';

/** Read the warning → prove it's you → confirm → read what happened. */
export type ICheckoutStep = 'confirm-intent' | 'verify' | 'confirm' | 'done';

/** The server's cap on the free-text reason, restated so the textarea agrees with it. */
export const CHECKOUT_REASON_MAX = 240;

export interface IUseCheckoutArgs {
	api: IEvaultApi;
	/** The ONE document leaving. A checkout is never a selection — see the modal. */
	document: IVaultDocument;
	/** 403 `EVAULT_NOT_ENTITLED` — a paywall is not a form error (R10). */
	onUnentitled: () => void;
	/** The list is out of date and the modal cannot fix it (409). */
	onRefresh: () => void;
}

/**
 * The two-call checkout (SIM-23 B3), shaped like `useTransfer` on purpose.
 *
 * Somebody who has driven a transfer should recognise every step of this: initiate
 * records intent, the face capture is minted against the pending record's id, and the
 * commit is what actually ends the vault's custody. The one state a transfer does not
 * have is `confirm-intent` — a screen whose entire job is to say, before anything is
 * created, that this removes the original permanently and that it can only come back as
 * a new re-vaulted iteration.
 *
 * CANCELLING IS A REAL CALL, for the reason `useTransfer` gives: a pending record left to
 * expire blocks the next attempt on the same document for fifteen minutes (the server
 * refuses a second pending checkout) and leaves the audit trail with an initiated event
 * that never ends.
 */
export const useCheckout = ({
	api,
	document,
	onUnentitled,
	onRefresh,
}: IUseCheckoutArgs) => {
	const [step, setStep] = useState<ICheckoutStep>('confirm-intent');
	const [reason, setReason] = useState('');
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState('');
	/**
	 * A 409 is terminal for this modal: the pending checkout is already committed,
	 * cancelled or expired, or the document is no longer the caller's to take. Neither
	 * Back nor Check out can do anything with it, so the buttons collapse to Close and
	 * the list refetches.
	 */
	const [conflicted, setConflicted] = useState(false);
	const [initiated, setInitiated] = useState<ICheckoutInitiated | null>(null);
	const [committed, setCommitted] = useState<ICheckoutCommitted | null>(null);
	/** Minted by `useFaceVerification` on the `verify` step; `commit` sends it back. */
	const [faceToken, setFaceToken] = useState('');
	/** Rounds of `commit` refused 403 `FACE_VERIFICATION_REQUIRED`. Same rule as the
	    transfer: the first sends the reader back to re-capture, and from the second on
	    re-capturing is not what is wrong, so the modal stops bouncing them. */
	const faceRefusals = useRef(0);

	/** `PATCH {action:'cancel'}`, best-effort: it resolves a record, not the UI. */
	const cancelPending = useCallback(async () => {
		if (!initiated || committed) return;
		await api.patch(`${EVAULT_API.VAULT_CHECKOUTS}/${initiated._id}`, {
			action: 'cancel',
		});
		setInitiated(null);
	}, [api, initiated, committed]);

	const initiate = useCallback(async () => {
		if (busy) return;
		setBusy(true);
		setError('');

		const result = await api.post<ICheckoutInitiated>(
			EVAULT_API.VAULT_CHECKOUTS,
			{ documentId: document._id, reason: reason.trim() || undefined }
		);
		setBusy(false);

		if (result.kind === 'unentitled') {
			onUnentitled();
			return;
		}
		if (result.kind !== 'ok') {
			/* Verbatim. Every refusal here names the thing that has to change — a legal
			   hold, an original that is already checked out, one that is not yours — and
			   a generic fallback would replace an instruction with a shrug. */
			setError(result.message);
			if (result.kind === 'conflict') onRefresh();
			return;
		}

		faceRefusals.current = 0;
		setFaceToken('');
		setInitiated(result.data);
		setStep('verify');
	}, [api, busy, document._id, onRefresh, onUnentitled, reason]);

	/** Back out of `verify` or `confirm` — resolves the pending record on the way. */
	const back = useCallback(async () => {
		if (busy) return;
		setBusy(true);
		await cancelPending();
		setBusy(false);
		setError('');
		setStep('confirm-intent');
	}, [busy, cancelPending]);

	/** The `verify` step's capture succeeded — move to the last confirmation. */
	const verified = useCallback((token: string) => {
		setFaceToken(token);
		setError('');
		setStep('confirm');
	}, []);

	const commit = useCallback(async () => {
		if (!initiated || busy) return;
		setBusy(true);
		setError('');

		const result = await api.patch<ICheckoutCommitted>(
			`${EVAULT_API.VAULT_CHECKOUTS}/${initiated._id}`,
			{ action: 'commit', faceVerification: faceToken }
		);
		setBusy(false);

		if (result.kind === 'unentitled') {
			onUnentitled();
			return;
		}
		if (result.kind === 'conflict') {
			setError(result.message);
			setConflicted(true);
			/* Whatever the server refused on — already committed, expired, or an original
			   that changed hands in another tab — the list on screen is describing a state
			   that no longer holds. */
			onRefresh();
			return;
		}
		if (
			result.kind === 'error' &&
			result.status === 403 &&
			result.message.startsWith('FACE_VERIFICATION_REQUIRED')
		) {
			const why = result.message.replace(/^FACE_VERIFICATION_REQUIRED:\s*/, '');
			faceRefusals.current += 1;
			setFaceToken('');
			if (faceRefusals.current <= 1) {
				setError(why);
				setStep('verify');
			} else {
				setError("Verification isn't being accepted. Close and try again.");
			}
			return;
		}
		if (result.kind !== 'ok') {
			setError(result.message);
			return;
		}

		setCommitted(result.data);
		setStep('done');
	}, [api, busy, faceToken, initiated, onRefresh, onUnentitled]);

	return {
		step,
		reason,
		setReason,
		busy,
		error,
		conflicted,
		initiated,
		committed,
		initiate,
		verified,
		back,
		commit,
		cancelPending,
	};
};
